'use client';

import React from 'react';

import Script from 'next/script';
import Header from '@/components/Header';
import Footer from '@/components/Footer';

export default function RequestMeetingPage() {
  return (
    <>
      <Script
        src="https://assets.calendly.com/assets/external/widget.js"
        strategy="afterInteractive"
      />
      <Header />
      {/* HERO */}
      <header
        className="py-20 pb-[220px]"
        style={{
          background:
            'radial-gradient(900px 500px at 85% -10%, rgba(201,137,65,0.15), transparent 60%), var(--navy-deep)',
        }}
      >
        <div className="max-w-site mx-auto px-7">
          <div className="inline-flex font-mono text-[12px] tracking-[0.18em] uppercase text-primary border border-primary/50 px-4 py-2 rounded-full mb-6">
            Free 30-minute strategy call
          </div>
          <h1 className="font-display font-bold uppercase text-white leading-[1.05]" style={{ fontSize: 'clamp(40px, 5vw, 64px)' }}>
            Pick a time.<br />Bring your ICP.
          </h1>
          <p className="mt-5 text-[19px] text-muted-cream leading-relaxed max-w-[60ch]">
            No pitch deck and no pressure. We map your Ideal Customer Profile, define what a
            qualified meeting means for your team, and tell you honestly if outbound is not your
            channel yet.
          </p>
        </div>
      </header>
      {/* BOOKING GRID */}
      <div
        className="max-w-site mx-auto px-7 pb-[90px] book-grid-responsive"
        style={{
          display: 'grid',
          gridTemplateColumns: '0.9fr 1.1fr',
          gap: 34,
          marginTop: -170,
          alignItems: 'start',
        }}
      >
        {/* Info Column */}
        <div className="flex flex-col gap-5">

          {/* Card 1 — What we will cover */}
          <div className="bg-white border border-line-color rounded-[14px] p-7 shadow-card">
            <span className="font-mono text-[11px] tracking-[0.18em] uppercase text-primary block mb-3">
              On the call
            </span>
            <h3 className="font-display font-semibold text-[20px] tracking-[0.02em] uppercase text-navy mb-3">
              What we will cover
            </h3>
            <ul className="list-none mt-1">
              {[
                'Your ICP, average deal size, and current pipeline sources',
                'How phone-first outbound would run in your market',
                'The qualified-meeting definition we would write into your agreement',
                'Team size, timeline, and what the first 60 to 90 days look like',
              ]?.map((item, i) => (
                <li
                  key={i}
                  className="relative pl-[30px] py-[7px] text-[14.5px] text-muted-text"
                >
                  <span
                    className="absolute left-0 top-[12px] w-[15px] h-[15px] rounded-[5px] bg-primary block"
                    style={{ clipPath: 'polygon(14% 44%,0 60%,40% 100%,100% 16%,84% 2%,40% 70%)' }}
                  />
                  {item}
                </li>
              ))}
            </ul>
          </div>

          {/* Card 2 — Come prepared */}
          <div className="bg-white border border-line-color rounded-[14px] p-7 shadow-card">
            <span className="font-mono text-[11px] tracking-[0.18em] uppercase text-primary block mb-3">
              Come prepared
            </span>
            <h3 className="font-display font-semibold text-[20px] tracking-[0.02em] uppercase text-navy mb-3">
              Helpful to have ready
            </h3>
            <ul className="list-none mt-1">
              {[
                'A rough picture of your ideal customer',
                'Your average deal size and sales cycle length',
                'Anything you have tried before: hires, agencies, or tools',
              ]?.map((item, i) => (
                <li
                  key={i}
                  className="relative pl-[30px] py-[7px] text-[14.5px] text-muted-text"
                >
                  <span
                    className="absolute left-0 top-[12px] w-[15px] h-[15px] rounded-[5px] bg-primary block"
                    style={{ clipPath: 'polygon(14% 44%,0 60%,40% 100%,100% 16%,84% 2%,40% 70%)' }}
                  />
                  {item}
                </li>
              ))}
            </ul>
            <p className="mt-3 text-[14.5px] text-muted-text">
              None of it is required. A conversation is enough to start.
            </p>
          </div>

          {/* Dark Card — Our commitment */}
          <div className="bg-navy border border-navy rounded-[14px] p-7 shadow-card">
            <h3 className="font-display font-semibold text-[20px] tracking-[0.02em] uppercase text-white mb-3">
              Our commitment
            </h3>
            <p className="text-[14.5px] text-muted-cream">
              <b className="text-primary">
                Published pricing, month to month, and a straight answer.
              </b>{' '}
              If your closers are not winning the meetings you already get, or outbound will not pay
              back at your deal size, we will say so on this call and part as friends.
            </p>
            <p className="mt-3 font-mono text-[12.5px] text-muted-cream">
              Prefer email first:{' '}
              <a
                href="mailto:william@commandva.com"
                className="text-primary hover:text-gold-bright transition-colors"
              >
                william@commandva.com
              </a>
            </p>
          </div>
        </div>

        {/* Calendly Card */}
        <div className="bg-white border border-line-color rounded-[18px] p-3.5 shadow-[0_26px_60px_rgba(18,36,47,0.16)] overflow-hidden">
          <div className="flex justify-between items-center px-4 py-3.5 border-b border-line-color font-mono text-[11.5px] tracking-[0.16em] uppercase text-muted-text">
            <span>SELECT A TIME</span>
            <b className="text-primary font-semibold">US CENTRAL AVAILABILITY</b>
          </div>
          <div
            className="calendly-inline-widget"
            data-url="https://calendly.com/william-commandva/commandva-strategy-call?hide_gdpr_banner=1&primary_color=c98941"
            style={{ minWidth: 300, height: 760, width: '100%' }}
          />
          <p className="text-center py-4 text-[13.5px] text-muted-text">
            If the calendar does not load,{' '}
            <a
              href="https://calendly.com/william-commandva/commandva-strategy-call"
              target="_blank"
              rel="noopener noreferrer"
              className="text-primary underline font-semibold hover:text-gold-bright transition-colors"
            >
              open the booking page directly
            </a>
            .
          </p>
        </div>
      </div>
      <Footer />
      <style>{`
        @media (max-width: 1020px) {
          .book-grid-responsive {
            grid-template-columns: 1fr !important;
          }
        }
      `}</style>
    </>
  );
}
